import { Suspense, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  AuthRequiredError,
  DefaultError,
  NetworkErrorScreen,
} from '@/shared/ui/ErrorScreen/ErrorScreen';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PermissionGuard } from '@/shared/ui/PermissionGuard/PermissionGuard';
import { labelByPath } from '@/widgets/Sidebar/sidebarConfig';
import SsoTokenProvider from '@/app/providers/SsoTokenProvider';
import { AppProviders } from '@/app/providers/AppProviders';
import { lazyDefault, lazyNamed } from '@/shared/lib/lazyLoad';
import { tokenStorage } from '@/shared/lib/tokenStorage';

const HomePage = lazyNamed(() => import('@/features/home/components/HomePage'), 'HomePage');
const CompanySalesStatus = lazyNamed(
  () => import('@/features/goal/components/CompanySalesStatus'),
  'CompanySalesStatus',
);
const ProjectList = lazyNamed(
  () => import('@/features/project/components/ProjectList'),
  'ProjectList',
);
const ProjectNumber = lazyNamed(
  () => import('@/features/project/components/ProjectNumber'),
  'ProjectNumber',
);
const ProjectDetail = lazyNamed(
  () => import('@/features/project/components/ProjectDetail'),
  'ProjectDetail',
);
const FinanceProjectManagement = lazyNamed(
  () => import('@/features/finance/components/FinanceProjectManagement'),
  'FinanceProjectManagement',
);
const PaymentStatus = lazyNamed(
  () => import('@/features/finance/components/PaymentStatus'),
  'PaymentStatus',
);
const DepartmentFinancialGoalManagement = lazyNamed(
  () => import('@/features/goal/components/DepartmentFinancialGoalManagement'),
  'DepartmentFinancialGoalManagement',
);
const PersonalGoalManagement = lazyNamed(
  () => import('@/features/goal/components/PersonalGoalManagement'),
  'PersonalGoalManagement',
);
const FinancialGoalManagement = lazyNamed(
  () => import('@/features/goal/components/FinancialGoalManagement'),
  'FinancialGoalManagement',
);
const ApprovalHome = lazyNamed(
  () => import('@/features/approval/components/ApprovalHome'),
  'ApprovalHome',
);
const ApprovalTemporary = lazyNamed(
  () => import('@/features/approval/components/ApprovalTemporary'),
  'ApprovalTemporary',
);
const ApprovalAll = lazyNamed(
  () => import('@/features/approval/components/ApprovalAll'),
  'ApprovalAll',
);
const ApprovalPending = lazyNamed(
  () => import('@/features/approval/components/ApprovalPending'),
  'ApprovalPending',
);
const ApprovalDocumentDetail = lazyNamed(
  () => import('@/features/approval/components/detail'),
  'ApprovalDocumentDetail',
);
const PtEstimateDraft = lazyNamed(
  () => import('@/features/approval/components/draft/PtEstimateDraft'),
  'PtEstimateDraft',
);
const PreOrderReviewDraft = lazyNamed(
  () => import('@/features/approval/components/draft/PreOrderReviewDraft'),
  'PreOrderReviewDraft',
);
const PostSettlementDraft = lazyNamed(
  () => import('@/features/approval/components/draft/PostSettlementDraft'),
  'PostSettlementDraft',
);
const OutsourcingPaymentDraft = lazyNamed(
  () => import('@/features/approval/components/draft/OutsourcingPaymentDraft'),
  'OutsourcingPaymentDraft',
);
const LaborPaymentDraft = lazyNamed(
  () => import('@/features/approval/components/draft/LaborPaymentDraft'),
  'LaborPaymentDraft',
);
const OperatingExpenseDraft = lazyNamed(
  () => import('@/features/approval/components/draft/OperatingExpenseDraft'),
  'OperatingExpenseDraft',
);
const TravelRequestDraft = lazyNamed(
  () => import('@/features/approval/components/draft/TravelRequestDraft'),
  'TravelRequestDraft',
);
const BusinessProposalDraft = lazyNamed(
  () => import('@/features/approval/components/draft/BusinessProposalDraft'),
  'BusinessProposalDraft',
);
const PurchaseRequestDraft = lazyNamed(
  () => import('@/features/approval/components/draft/PurchaseRequestDraft'),
  'PurchaseRequestDraft',
);
const CorporateCardDraft = lazyNamed(
  () => import('@/features/approval/components/draft/CorporateCardDraft'),
  'CorporateCardDraft',
);
const CorporateCardSharedDraft = lazyNamed(
  () => import('@/features/approval/components/draft/CorporateCardSharedDraft'),
  'CorporateCardSharedDraft',
);
const PersonalCardDraft = lazyNamed(
  () => import('@/features/approval/components/draft/PersonalCardDraft'),
  'PersonalCardDraft',
);
const CorporateSealDraft = lazyNamed(
  () => import('@/features/approval/components/draft/CorporateSealDraft'),
  'CorporateSealDraft',
);
const PaymentStatusReportDraft = lazyNamed(
  () => import('@/features/approval/components/draft/PaymentStatusReportDraft'),
  'PaymentStatusReportDraft',
);
const ClientRegistrationDraft = lazyNamed(
  () => import('@/features/approval/components/draft/ClientRegistrationDraft'),
  'ClientRegistrationDraft',
);
const RetirementPaymentDraft = lazyNamed(
  () => import('@/features/approval/components/draft/RetirementPaymentDraft'),
  'RetirementPaymentDraft',
);
const PersonnelListPage = lazyNamed(
  () => import('@/features/hrup/components/PersonnelManagement'),
  'PersonnelListPage',
);
const ResignedPersonnelListPage = lazyNamed(
  () => import('@/features/hrup/components/PersonnelManagement'),
  'ResignedPersonnelListPage',
);
const DepartmentMoveHistoryPage = lazyNamed(
  () => import('@/features/hrup/components/PersonnelManagement'),
  'DepartmentMoveHistoryPage',
);
const ChangeHistoryPage = lazyNamed(
  () => import('@/features/hrup/components/ChangeHistory'),
  'ChangeHistoryPage',
);
const DepartmentManagementPage = lazyNamed(
  () => import('@/features/hrup/components/DepartmentManagement'),
  'DepartmentManagementPage',
);
const ApprovalTemplateManagementPage = lazyNamed(
  () => import('@/features/hrup/components/ApprovalTemplateManagement'),
  'ApprovalTemplateManagementPage',
);
const OfficialSchedulePage = lazyNamed(
  () => import('@/features/hrup/components/OfficialSchedule'),
  'OfficialSchedulePage',
);
const BoardHome = lazyNamed(
  () => import('@/features/board/components/BoardHome/BoardHome'),
  'BoardHome',
);
const BoardList = lazyNamed(
  () => import('@/features/board/components/BoardList/BoardList'),
  'BoardList',
);
const BoardDetail = lazyNamed(
  () => import('@/features/board/components/BoardDetail/BoardDetail'),
  'BoardDetail',
);
const BoardEditor = lazyNamed(
  () => import('@/features/board/components/BoardEditor/BoardEditor'),
  'BoardEditor',
);
const AppLayout = lazyDefault(() => import('../layouts/AppLayout'));

// 로컬 `vite dev` 전용 계정 전환 위젯. dynamic import 를 import.meta.env.DEV 분기 안에 두어
// prod 빌드에서는 정적 제거(DCE)되게 한다 — 청크·엔드포인트 문자열이 산출물에 남지 않음.
const DevLoginWidget = import.meta.env.DEV
  ? lazyNamed(() => import('@/app/dev/DevLoginWidget'), 'DevLoginWidget')
  : null;

type RouteConfig = {
  path?: string;
  index?: boolean;
  element: ReactElement;
  handlePath: string;
};

type RouteHandle = { title: string };

const routeHandle = (path: string, fallback?: string): RouteHandle | undefined => {
  const title = labelByPath[path] ?? fallback;
  return title ? { title } : undefined;
};

const projectRoutes: RouteConfig[] = [
  { index: true, element: <Navigate to="/home" replace />, handlePath: '/' },
  // '/home' 직접 URL 접근(북마크 호환)도 유지. PermissionGuard 없음(기존 홈도 없었음).
  { path: 'home', element: <HomePage />, handlePath: '/home' },
  {
    path: 'projects',
    element: (
      <PermissionGuard permission="projects" deniedMessage="프로젝트 메뉴에 접근 권한이 없습니다.">
        <ProjectList />
      </PermissionGuard>
    ),
    handlePath: '/projects',
  },
  {
    path: 'projects/numbers',
    element: (
      <PermissionGuard
        permission="projectNumbers"
        deniedMessage="프로젝트 번호 메뉴에 접근 권한이 없습니다."
      >
        <ProjectNumber />
      </PermissionGuard>
    ),
    handlePath: '/projects/numbers',
  },
  {
    path: 'projects/new',
    element: <Navigate to="/projects" replace />,
    handlePath: '/projects/new',
  },
  {
    path: 'projects/:id',
    element: (
      <PermissionGuard permission="projects" deniedMessage="프로젝트 메뉴에 접근 권한이 없습니다.">
        <ProjectDetail />
      </PermissionGuard>
    ),
    handlePath: '/projects/:id',
  },
];

const goalRoutes: RouteConfig[] = [
  {
    path: 'goals/company-goal',
    element: (
      <PermissionGuard
        permission="goals.company"
        deniedMessage="전사 매출현황 메뉴에 접근 권한이 없습니다."
      >
        <CompanySalesStatus />
      </PermissionGuard>
    ),
    handlePath: '/goals/company-goal',
  },
  {
    path: 'goals/department-financial-goals',
    element: (
      <PermissionGuard
        permission="goals.departments"
        deniedMessage="부서 목표관리 메뉴에 접근 권한이 없습니다."
      >
        <DepartmentFinancialGoalManagement />
      </PermissionGuard>
    ),
    handlePath: '/goals/department-financial-goals',
  },
  {
    path: 'goals/personal-goal',
    element: (
      <PermissionGuard
        permission="goals.personal"
        deniedMessage="프로젝트 기여도 메뉴에 접근 권한이 없습니다."
      >
        <PersonalGoalManagement />
      </PermissionGuard>
    ),
    handlePath: '/goals/personal-goal',
  },
];

const financeRoutes: RouteConfig[] = [
  {
    path: 'fns/accounting-number',
    element: (
      <PermissionGuard permission="fns" deniedMessage="F&S 메뉴에 접근 권한이 없습니다.">
        <FinanceProjectManagement />
      </PermissionGuard>
    ),
    handlePath: '/fns/accounting-number',
  },
  {
    path: 'fns/financial-goals',
    element: (
      <PermissionGuard permission="fns" deniedMessage="F&S 메뉴에 접근 권한이 없습니다.">
        <FinancialGoalManagement />
      </PermissionGuard>
    ),
    handlePath: '/fns/financial-goals',
  },
  {
    path: 'fns/payment-status',
    element: (
      <PermissionGuard
        permission="fnsPaymentStatus"
        deniedMessage="외주비 현황 메뉴에 접근 권한이 없습니다."
      >
        <PaymentStatus />
      </PermissionGuard>
    ),
    handlePath: '/fns/payment-status',
  },
];

const approvalHomeRoutes: RouteConfig[] = [
  {
    path: 'approval/home',
    element: (
      <PermissionGuard permission="approval" deniedMessage="전자결재 메뉴에 접근 권한이 없습니다.">
        <ApprovalHome />
      </PermissionGuard>
    ),
    handlePath: '/approval/home',
  },
  {
    path: 'approval/pending',
    element: (
      <PermissionGuard permission="approval" deniedMessage="전자결재 메뉴에 접근 권한이 없습니다.">
        <ApprovalPending />
      </PermissionGuard>
    ),
    handlePath: '/approval/pending',
  },
  {
    path: 'approval/pending/:documentId',
    element: (
      <PermissionGuard permission="approval" deniedMessage="전자결재 메뉴에 접근 권한이 없습니다.">
        <ApprovalDocumentDetail />
      </PermissionGuard>
    ),
    handlePath: '/approval/pending/:documentId',
  },
];

const approvalDocumentRoutes: RouteConfig[] = [
  {
    path: 'approval/documents/temporary',
    element: (
      <PermissionGuard permission="approval" deniedMessage="전자결재 메뉴에 접근 권한이 없습니다.">
        <ApprovalTemporary />
      </PermissionGuard>
    ),
    handlePath: '/approval/documents/temporary',
  },
  {
    path: 'approval/documents/all',
    element: (
      <PermissionGuard permission="approval" deniedMessage="전자결재 메뉴에 접근 권한이 없습니다.">
        <ApprovalAll />
      </PermissionGuard>
    ),
    handlePath: '/approval/documents/all',
  },
  // Dynamic route must come LAST to avoid matching static paths like 'draft', 'temporary', etc.
  {
    path: 'approval/documents/:documentId',
    element: (
      <PermissionGuard permission="approval" deniedMessage="전자결재 메뉴에 접근 권한이 없습니다.">
        <ApprovalDocumentDetail />
      </PermissionGuard>
    ),
    handlePath: '/approval/documents/:documentId',
  },
];

const guardedBoard = (el: ReactElement): ReactElement => (
  <PermissionGuard permission="board" deniedMessage="게시판 메뉴에 접근 권한이 없습니다.">
    {el}
  </PermissionGuard>
);

const boardRoutes: RouteConfig[] = [
  { path: 'boards', element: guardedBoard(<BoardHome />), handlePath: '/boards' },
  {
    path: 'boards/:boardType',
    element: guardedBoard(<BoardList />),
    handlePath: '/boards/:boardType',
  },
  {
    path: 'boards/:boardType/write',
    element: guardedBoard(<BoardEditor />),
    handlePath: '/boards/:boardType/write',
  },
  {
    path: 'boards/:boardType/write/:id',
    element: guardedBoard(<BoardEditor />),
    handlePath: '/boards/:boardType/write/:id',
  },
  {
    path: 'boards/:boardType/:id',
    element: guardedBoard(<BoardDetail />),
    handlePath: '/boards/:boardType/:id',
  },
];

// 서브 메뉴 권한 단일 관리 결정: 조직관리/부서관리/결재선 관리 등을 개별 action 으로
// 세분화하지 않고, `hrup` 단일 permission(hrup.* action any)으로 라우트 가드·메뉴 노출을 통합한다.
const hrupRoutes: RouteConfig[] = [
  {
    path: 'hrup/personnel',
    element: (
      <PermissionGuard permission="hrup" deniedMessage="HRup 메뉴에 접근 권한이 없습니다.">
        <PersonnelListPage />
      </PermissionGuard>
    ),
    handlePath: '/hrup/personnel',
  },
  {
    path: 'hrup/personnel/resigned',
    element: (
      <PermissionGuard permission="hrup" deniedMessage="HRup 메뉴에 접근 권한이 없습니다.">
        <ResignedPersonnelListPage />
      </PermissionGuard>
    ),
    handlePath: '/hrup/personnel/resigned',
  },
  {
    path: 'hrup/personnel/department-moves',
    element: (
      <PermissionGuard permission="hrup" deniedMessage="HRup 메뉴에 접근 권한이 없습니다.">
        <DepartmentMoveHistoryPage />
      </PermissionGuard>
    ),
    handlePath: '/hrup/personnel/department-moves',
  },
  {
    path: 'hrup/departments',
    element: (
      <PermissionGuard permission="hrup" deniedMessage="HRup 메뉴에 접근 권한이 없습니다.">
        <DepartmentManagementPage />
      </PermissionGuard>
    ),
    handlePath: '/hrup/departments',
  },
  {
    path: 'hrup/approval-line',
    element: (
      <PermissionGuard permission="hrup" deniedMessage="결재선 관리 권한이 없습니다.">
        <ApprovalTemplateManagementPage />
      </PermissionGuard>
    ),
    handlePath: '/hrup/approval-line',
  },
  {
    path: 'hrup/schedule',
    element: (
      <PermissionGuard permission="hrup" deniedMessage="HRup 메뉴에 접근 권한이 없습니다.">
        <OfficialSchedulePage />
      </PermissionGuard>
    ),
    handlePath: '/hrup/schedule',
  },
  // 변경 이력 독립 경로 — 조직관리 화면 폐기 후 유일한 접근 경로.
  {
    path: 'hrup/history',
    element: (
      <PermissionGuard permission="hrup" deniedMessage="변경 이력 권한이 없습니다.">
        <ChangeHistoryPage />
      </PermissionGuard>
    ),
    handlePath: '/hrup/history',
  },
];

const guardedDraft = (el: ReactElement): ReactElement => (
  <PermissionGuard permission="approval.create" deniedMessage="기안 작성 권한이 없습니다.">
    {el}
  </PermissionGuard>
);

const approvalDraftRoutes: RouteConfig[] = [
  {
    path: 'approval/draft/pt-estimate',
    element: guardedDraft(<PtEstimateDraft />),
    handlePath: '/approval/draft/pt-estimate',
  },
  {
    path: 'approval/draft/pre-order-review',
    element: guardedDraft(<PreOrderReviewDraft />),
    handlePath: '/approval/draft/pre-order-review',
  },
  {
    path: 'approval/draft/post-settlement',
    element: guardedDraft(<PostSettlementDraft />),
    handlePath: '/approval/draft/post-settlement',
  },
  {
    path: 'approval/draft/outsourcing-payment',
    element: guardedDraft(<OutsourcingPaymentDraft />),
    handlePath: '/approval/draft/outsourcing-payment',
  },
  {
    path: 'approval/draft/labor-payment',
    element: guardedDraft(<LaborPaymentDraft />),
    handlePath: '/approval/draft/labor-payment',
  },
  {
    path: 'approval/draft/operating-expense',
    element: guardedDraft(<OperatingExpenseDraft />),
    handlePath: '/approval/draft/operating-expense',
  },
  {
    path: 'approval/draft/travel-request',
    element: guardedDraft(<TravelRequestDraft />),
    handlePath: '/approval/draft/travel-request',
  },
  {
    path: 'approval/draft/business-draft',
    element: guardedDraft(<BusinessProposalDraft />),
    handlePath: '/approval/draft/business-draft',
  },
  {
    path: 'approval/draft/purchase-request',
    element: guardedDraft(<PurchaseRequestDraft />),
    handlePath: '/approval/draft/purchase-request',
  },
  {
    path: 'approval/draft/corporate-card-personal',
    element: guardedDraft(<CorporateCardDraft />),
    handlePath: '/approval/draft/corporate-card-personal',
  },
  {
    path: 'approval/draft/corporate-card-shared',
    element: guardedDraft(<CorporateCardSharedDraft />),
    handlePath: '/approval/draft/corporate-card-shared',
  },
  {
    path: 'approval/draft/personal-card',
    element: guardedDraft(<PersonalCardDraft />),
    handlePath: '/approval/draft/personal-card',
  },
  {
    path: 'approval/draft/corporate-seal',
    element: guardedDraft(<CorporateSealDraft />),
    handlePath: '/approval/draft/corporate-seal',
  },
  {
    path: 'approval/draft/cash-collection-report',
    element: guardedDraft(<PaymentStatusReportDraft />),
    handlePath: '/approval/draft/cash-collection-report',
  },
  {
    path: 'approval/draft/customer-registration',
    element: guardedDraft(<ClientRegistrationDraft />),
    handlePath: '/approval/draft/customer-registration',
  },
  {
    path: 'approval/draft/severance-payment',
    element: guardedDraft(<RetirementPaymentDraft />),
    handlePath: '/approval/draft/severance-payment',
  },
];

const renderRoutes = (configs: RouteConfig[]) =>
  configs.map(({ path, index, element, handlePath }) => (
    <Route
      key={path ?? 'index'}
      path={path}
      index={index}
      element={element}
      handle={routeHandle(handlePath, path)}
    />
  ));

const NotFoundRoute = (
  <Route
    path="*"
    element={<DefaultError message="페이지를 찾을 수 없습니다." />}
    handle={routeHandle('*', '페이지를 찾을 수 없습니다.')}
  />
);

export const AppRoutes = () => (
  <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route element={<AppLayout />}>
        {renderRoutes(projectRoutes)}
        {renderRoutes(goalRoutes)}
        {renderRoutes(financeRoutes)}
        {renderRoutes(approvalHomeRoutes)}
        {renderRoutes(approvalDocumentRoutes)}
        {renderRoutes(approvalDraftRoutes)}
        {renderRoutes(boardRoutes)}
        {renderRoutes(hrupRoutes)}
        {NotFoundRoute}
      </Route>
    </Routes>
  </Suspense>
);

const reloadApp = () => window.location.reload();

export const SsoTokenGate = () => {
  return (
    <AppProviders>
      <SsoTokenProvider>
        {(error) => {
          if (error) {
            if (error.type === 'network') {
              return <NetworkErrorScreen onRetry={reloadApp} />;
            }
            return <DefaultError message={error.message} onRetry={reloadApp} />;
          }

          const token = tokenStorage.getToken();
          if (!token) {
            return <AuthRequiredError onRetry={reloadApp} />;
          }

          return <AppRoutes />;
        }}
      </SsoTokenProvider>
      {DevLoginWidget ? (
        <Suspense fallback={null}>
          <DevLoginWidget />
        </Suspense>
      ) : null}
    </AppProviders>
  );
};
